import React, { useState } from 'react';
import { applications, jobListings } from '../mockData';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from '../hooks/use-toast';
import { Eye, CheckCircle, XCircle, Clock } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const CompanyApplicationsPage = () => {
  // Mock: Filter applications for jobs posted by this company
  const companyJobs = jobListings.slice(0, 3);
  const companyApplications = applications; // In real app, filter by company's jobs
  
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      reviewing: 'bg-[#E0E8D0] text-[#0A3900]',
      accepted: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'reviewing': return <Eye className="w-4 h-4" />;
      case 'accepted': return <CheckCircle className="w-4 h-4" />;
      case 'rejected': return <XCircle className="w-4 h-4" />;
      default: return null;
    }
  };

  const handleViewApplication = (application) => {
    setSelectedApplication(application);
    setDialogOpen(true);
  };

  const handleStatusChange = (applicationId, newStatus) => {
    toast({
      title: 'Status updated',
      description: `Application status changed to ${newStatus}`
    });
    setDialogOpen(false);
  };

  const filteredApplications = statusFilter === 'all' 
    ? companyApplications 
    : companyApplications.filter(app => app.status === statusFilter);

  const stats = {
    total: companyApplications.length,
    pending: companyApplications.filter(a => a.status === 'pending').length,
    reviewing: companyApplications.filter(a => a.status === 'reviewing').length,
    accepted: companyApplications.filter(a => a.status === 'accepted').length,
    rejected: companyApplications.filter(a => a.status === 'rejected').length
  };

  return (
    <div className="min-h-screen bg-[#F9FBF5] pt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Applicant Tracking System</h1>
          <p className="text-gray-600">Manage applications received for your job postings</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 sm:gap-4 mb-8">
          <Card className="p-4">
            <div className="text-2xl font-bold text-gray-900 mb-1">{stats.total}</div>
            <div className="text-sm text-gray-600">Total</div>
          </Card>
          <Card className="p-4 border-yellow-200 bg-yellow-50">
            <div className="text-2xl font-bold text-yellow-700 mb-1">{stats.pending}</div>
            <div className="text-sm text-yellow-700">Pending</div>
          </Card>
          <Card className="p-4 border-[#B8C4AD] bg-[#F1F5E6]">
            <div className="text-2xl font-bold text-[#0A3900] mb-1">{stats.reviewing}</div>
            <div className="text-sm text-[#0A3900]">Reviewing</div>
          </Card>
          <Card className="p-4 border-green-200 bg-green-50">
            <div className="text-2xl font-bold text-green-700 mb-1">{stats.accepted}</div>
            <div className="text-sm text-green-700">Accepted</div>
          </Card>
          <Card className="p-4 border-red-200 bg-red-50">
            <div className="text-2xl font-bold text-red-700 mb-1">{stats.rejected}</div>
            <div className="text-sm text-red-700">Rejected</div>
          </Card>
        </div>

        {/* Filters */}
        <Tabs value={statusFilter} onValueChange={setStatusFilter} className="mb-6">
          <TabsList>
            <TabsTrigger value="all">All Applications</TabsTrigger>
            <TabsTrigger value="pending">Pending</TabsTrigger>
            <TabsTrigger value="reviewing">Reviewing</TabsTrigger>
            <TabsTrigger value="accepted">Accepted</TabsTrigger>
            <TabsTrigger value="rejected">Rejected</TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Applications Table */}
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Candidate
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Position
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Applied Date
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredApplications.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                      No applications found
                    </td>
                  </tr>
                ) : (
                  filteredApplications.map((application) => (
                    <tr key={application.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <img
                            src={`https://i.pravatar.cc/150?u=${application.candidateEmail}`}
                            alt={application.candidateName}
                            className="w-10 h-10 rounded-full"
                          />
                          <div>
                            <div className="font-medium text-gray-900">{application.candidateName}</div>
                            <div className="text-sm text-gray-500">{application.candidateEmail}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">{application.job.title}</div>
                        <div className="text-sm text-gray-500">
                          ${application.job.payRange.min}-${application.job.payRange.max}/hr
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Badge className={`${getStatusColor(application.status)} flex items-center gap-1 w-fit`}>
                          {getStatusIcon(application.status)}
                          {application.status.charAt(0).toUpperCase() + application.status.slice(1)}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {application.appliedAt}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewApplication(application)}
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          Review
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Application Detail Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            {selectedApplication && (
              <>
                <DialogHeader>
                  <DialogTitle>Application Review</DialogTitle>
                  <DialogDescription>
                    Review and manage this job application
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-6 mt-4">
                  {/* Candidate Info */}
                  <div className="flex items-start gap-4 pb-6 border-b">
                    <img
                      src={`https://i.pravatar.cc/150?u=${selectedApplication.candidateEmail}`}
                      alt={selectedApplication.candidateName}
                      className="w-16 h-16 rounded-full"
                    />
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-gray-900">
                        {selectedApplication.candidateName}
                      </h3>
                      <p className="text-gray-600">{selectedApplication.candidateEmail}</p>
                      <Badge className={`${getStatusColor(selectedApplication.status)} mt-2`}>
                        {selectedApplication.status}
                      </Badge>
                    </div>
                  </div>

                  {/* Job Info */}
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Applied For:</h4>
                    <p className="text-lg text-gray-900">{selectedApplication.job.title}</p>
                    <p className="text-sm text-gray-600 mt-1">
                      ${selectedApplication.job.payRange.min}-${selectedApplication.job.payRange.max}/hr • 
                      {selectedApplication.job.location} • {selectedApplication.job.type}
                    </p>
                  </div>

                  {/* Cover Letter */}
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Cover Letter:</h4>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-gray-700 whitespace-pre-wrap">{selectedApplication.coverLetter}</p>
                    </div>
                  </div>

                  {/* Application Date */}
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Application Date:</h4>
                    <p className="text-gray-700">{selectedApplication.appliedAt}</p>
                  </div>

                  {/* Status Management */}
                  <div className="pt-6 border-t">
                    <h4 className="font-semibold text-gray-900 mb-4">Update Application Status:</h4>
                    <div className="flex gap-3">
                      <Button
                        variant="outline"
                        className="flex-1"
                        onClick={() => handleStatusChange(selectedApplication.id, 'reviewing')}
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        Mark as Reviewing
                      </Button>
                      <Button
                        className="flex-1 bg-green-600 hover:bg-green-700"
                        onClick={() => handleStatusChange(selectedApplication.id, 'accepted')}
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Accept
                      </Button>
                      <Button
                        variant="destructive"
                        className="flex-1"
                        onClick={() => handleStatusChange(selectedApplication.id, 'rejected')}
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Reject
                      </Button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default CompanyApplicationsPage;
